import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Mail, Lock, User, Eye, EyeOff, Calendar, Gamepad2, Phone, CheckCircle2, MailCheck } from 'lucide-react';
import api from '../lib/api';

const PLAYER_ROLES = [
  { value: 'GK', label: 'GK - Portiere' },
  { value: 'DC', label: 'DC - Difensore Centrale' },
  { value: 'CDC', label: 'CDC - Centrocampista Difensivo' },
  { value: 'ES', label: 'ES - Esterno Sinistro' },
  { value: 'ED', label: 'ED - Esterno Destro' },
  { value: 'COC', label: 'COC - Centrocampista Offensivo' },
  { value: 'ATT', label: 'ATT - Attaccante' },
];

const Login = () => {
  const { t, i18n } = useTranslation();
  const { loginWithGoogle, setSession } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isItalian = i18n.language === 'it';
  
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [formData, setFormData] = useState({
    gamertag: '',
    email: '',
    password: '',
    confirmPassword: '',
    playerRole: '',
    birthYear: '',
    phone: ''
  });

  // Show toast if user just verified their email
  useEffect(() => {
    if (searchParams.get('verified') === 'true') {
      toast.success(isItalian ? 'Email verificata con successo! Ora puoi accedere.' : 'Email verified! You can now sign in.');
    }
  }, [searchParams, isItalian]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleResendVerification = async () => {
    if (!registeredEmail) return;
    setResendLoading(true);
    try {
      await api.post('/auth/resend-verification', { email: registeredEmail });
      toast.success(isItalian ? 'Nuovo link di verifica inviato!' : 'New verification link sent!');
    } catch {
      toast.error(isItalian ? 'Errore nell\'invio. Riprova.' : 'Send error. Try again.');
    }
    setResendLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegister) {
        if (formData.password !== formData.confirmPassword) {
          toast.error(isItalian ? 'Le password non coincidono' : 'Passwords do not match');
          setLoading(false);
          return;
        }
        if (formData.password.length < 6) {
          toast.error(isItalian ? 'La password deve avere almeno 6 caratteri' : 'Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        
        const res = await api.post('/auth/register', {
          gamertag: formData.gamertag,
          email: formData.email,
          password: formData.password,
          player_role: formData.playerRole,
          birth_year: formData.birthYear ? parseInt(formData.birthYear) : null,
          phone: formData.phone
        });
        
        // Show verification screen
        if (res.data.requires_verification) {
          setRegisteredEmail(formData.email);
          setVerificationSent(true);
        } else if (res.data.token) {
          setSession(res.data.token, res.data.user);
          toast.success(isItalian ? 'Registrazione completata!' : 'Registration complete!');
          navigate('/dashboard');
        } else {
          toast.success(isItalian ? 'Registrazione completata! Effettua il login.' : 'Registration complete! Please login.');
          setIsRegister(false);
        }
      } else {
        const res = await api.post('/auth/login', {
          email: formData.email,
          password: formData.password
        });
        setSession(res.data.token, res.data.user);
        toast.success(isItalian ? 'Login effettuato!' : 'Logged in!');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Auth error:', error);
      let errorMessage = isItalian ? 'Errore di autenticazione' : 'Authentication error';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(d => d.msg || d).join(', ');
        } else if (typeof detail === 'object' && detail.msg) {
          errorMessage = detail.msg;
        }
      } else if (!error.response) {
        errorMessage = isItalian
          ? 'Errore di connessione al server. Controlla la tua connessione internet e riprova.'
          : 'Connection error. Check your internet and try again.';
      }
      toast.error(errorMessage);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen ept-bg-ambient flex relative overflow-hidden">
      {/* Background grid */}
      <div className="absolute inset-0 grid-bg pointer-events-none" />

      {/* Left - Form Panel */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-8 relative z-10">
        {/* Ambient glow */}
        <div className="absolute top-1/4 left-1/3 w-[400px] h-[400px] bg-[#00F0FF]/5 rounded-full blur-[120px] pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          {/* Email Verification Sent Screen */}
          {verificationSent ? (
            <div className="glass p-8 border border-[#00F0FF]/15 text-center" style={{ boxShadow: '0 0 30px rgba(0,240,255,0.05)' }}>
              <div className="flex justify-center mb-5">
                <div className="w-16 h-16 rounded-full bg-[#00F0FF]/10 flex items-center justify-center border border-[#00F0FF]/20">
                  <MailCheck className="w-8 h-8 text-[#00F0FF]" />
                </div>
              </div>
              <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-2" data-testid="verification-title">
                {isItalian ? 'Controlla la tua Email' : 'Check your Email'}
              </h2>
              <p className="text-slate-400 text-sm mb-4 leading-relaxed">
                {isItalian 
                  ? <>Abbiamo inviato un link di verifica a<br /><span className="text-[#00F0FF] font-semibold">{registeredEmail}</span><br />Clicca il link nell'email per attivare il tuo account.</>
                  : <>We sent a verification link to<br /><span className="text-[#00F0FF] font-semibold">{registeredEmail}</span><br />Click the link to activate your account.</>
                }
              </p>
              <div className="w-full h-px bg-[#00F0FF]/10 my-5" />
              <p className="text-slate-500 text-xs mb-3">
                {isItalian ? 'Non hai ricevuto l\'email?' : "Didn't receive it?"}
              </p>
              <Button
                onClick={handleResendVerification}
                disabled={resendLoading}
                variant="outline"
                data-testid="resend-verification-btn"
                className="h-10 text-sm border-[#00F0FF]/20 text-[#00F0FF] hover:bg-[#00F0FF]/10"
              >
                {resendLoading 
                  ? <div className="w-4 h-4 border-2 border-[#00F0FF]/30 border-t-[#00F0FF] rounded-full animate-spin" />
                  : (isItalian ? 'Reinvia Email' : 'Resend Email')
                }
              </Button>
              <div className="mt-5">
                <button
                  type="button"
                  onClick={() => { setVerificationSent(false); setIsRegister(false); }}
                  data-testid="back-to-login-btn"
                  className="text-sm text-slate-500 hover:text-[#00F0FF] transition-colors"
                >
                  {isItalian ? 'Torna al Login' : 'Back to Login'}
                </button>
              </div>
            </div>
          ) : (
          <>
          {/* Logo */}
          <Link to="/" className="flex items-center justify-center gap-3 mb-8" data-testid="auth-logo">
            <img 
              src="/ept-logo.png" 
              alt="EPT Italy" 
              className="h-14 w-auto object-contain"
              style={{ filter: 'drop-shadow(0 0 12px rgba(0,240,255,0.4))' }}
            />
            <span className="font-heading font-bold text-2xl uppercase tracking-wider neon-text-cyan">
              EPT ITALY
            </span>
          </Link>

          {/* Glass card form container */}
          <div className="glass p-6 md:p-8 border border-[#00F0FF]/15" style={{ boxShadow: '0 0 30px rgba(0,240,255,0.05)' }}>
            <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight text-white mb-1 text-center" data-testid="auth-title">
              {isRegister 
                ? (isItalian ? 'Registrati' : 'Register')
                : (isItalian ? 'Accedi' : 'Sign In')
              }
            </h1>
            <p className="text-slate-500 mb-6 text-center text-sm">
              {isRegister 
                ? (isItalian ? 'Crea il tuo account per iniziare' : 'Create your account to get started')
                : (isItalian ? 'Accedi al tuo account' : 'Sign in to your account')
              }
            </p>

            <form onSubmit={handleSubmit} className="space-y-3" data-testid="auth-form">
              {/* Google Login */}
              <div className="mb-1">
                <button
                  type="button"
                  onClick={loginWithGoogle}
                  data-testid="google-login-btn"
                  className="w-full h-11 flex items-center justify-center gap-3 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-none transition-all duration-150 border border-white/10 hover:border-white/20 hover:shadow-[0_0_10px_rgba(255,255,255,0.05)]"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  {isItalian ? 'Continua con Google' : 'Continue with Google'}
                </button>
                <button
                  type="button"
                  onClick={() => { window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/discord/login`; }}
                  data-testid="discord-login-btn"
                  className="w-full h-11 mt-2 flex items-center justify-center gap-3 bg-[#5865F2]/15 hover:bg-[#5865F2]/25 text-white font-semibold rounded-none transition-all duration-150 border border-[#5865F2]/40 hover:border-[#5865F2]/60"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#5865F2">
                    <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                  </svg>
                  {isItalian ? 'Continua con Discord' : 'Continue with Discord'}
                </button>
                <div className="flex items-center gap-4 my-4">
                  <div className="flex-1 h-px bg-[#00F0FF]/10" />
                  <span className="text-xs text-slate-600 uppercase tracking-wider">{isItalian ? 'oppure' : 'or'}</span>
                  <div className="flex-1 h-px bg-[#00F0FF]/10" />
                </div>
              </div>

              {isRegister && (
                <>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#00F0FF]/50" />
                    <Input
                      name="gamertag"
                      type="text"
                      placeholder="Gamertag"
                      value={formData.gamertag}
                      onChange={handleChange}
                      required
                      data-testid="register-gamertag"
                      className="pl-10 h-11"
                    />
                  </div>
                  
                  <div className="relative">
                    <Gamepad2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#00F0FF]/50" />
                    <select
                      name="playerRole"
                      value={formData.playerRole}
                      onChange={handleChange}
                      required
                      data-testid="register-role"
                      className="w-full pl-10 h-11 bg-[#050508] border border-[#00F0FF]/15 text-white rounded-none appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/50 focus:border-[#00F0FF]/40 transition-all duration-150"
                    >
                      <option value="" className="bg-[#0a0d15]">{isItalian ? 'Seleziona Ruolo' : 'Select Role'}</option>
                      {PLAYER_ROLES.map(role => (
                        <option key={role.value} value={role.value} className="bg-[#0a0d15]">
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#00F0FF]/50" />
                    <Input
                      name="birthYear"
                      type="number"
                      placeholder={isItalian ? 'Anno di Nascita (es. 1995)' : 'Birth Year (e.g. 1995)'}
                      value={formData.birthYear}
                      onChange={handleChange}
                      min="1950"
                      max={new Date().getFullYear() - 10}
                      required
                      data-testid="register-birthyear"
                      className="pl-10 h-11"
                    />
                  </div>
                  
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#00F0FF]/50" />
                    <Input
                      name="phone"
                      type="tel"
                      placeholder={isItalian ? 'Numero di Telefono' : 'Phone Number'}
                      value={formData.phone}
                      onChange={handleChange}
                      data-testid="register-phone"
                      className="pl-10 h-11"
                    />
                  </div>
                </>
              )}
              
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#00F0FF]/50" />
                <Input
                  name="email"
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="auth-email"
                  className="pl-10 h-11"
                />
              </div>
              
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#00F0FF]/50" />
                <Input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="auth-password"
                  className="pl-10 pr-10 h-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-[#00F0FF] transition-colors"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              
              {isRegister && (
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#00F0FF]/50" />
                  <Input
                    name="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={isItalian ? 'Conferma Password' : 'Confirm Password'}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    data-testid="register-confirm-password"
                    className="pl-10 h-11"
                  />
                </div>
              )}

              {!isRegister && (
                <div className="text-right -mt-1">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-xs text-[#00F0FF]/70 hover:text-[#00F0FF] transition-colors"
                    data-testid="forgot-password-link"
                  >
                    {isItalian ? 'Password dimenticata?' : 'Forgot password?'}
                  </button>
                </div>
              )}
              
              <Button
                type="submit"
                disabled={loading}
                data-testid="auth-submit-btn"
                className="w-full h-11 btn-primary text-base font-bold uppercase tracking-wider mt-2"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-[#050508]/30 border-t-[#050508] rounded-full animate-spin" />
                ) : (
                  isRegister 
                    ? (isItalian ? 'Registrati' : 'Register')
                    : (isItalian ? 'Accedi' : 'Sign In')
                )}
              </Button>
            </form>
          </div>

          {/* Toggle */}
          <p className="text-center text-slate-500 mt-6 text-sm">
            {isRegister 
              ? (isItalian ? 'Hai gia un account?' : 'Already have an account?')
              : (isItalian ? 'Non hai un account?' : "Don't have an account?")
            }
            <button
              type="button"
              onClick={() => setIsRegister(!isRegister)}
              data-testid="auth-toggle"
              className="ml-2 text-[#00F0FF] hover:text-[#00F0FF]/80 font-semibold uppercase tracking-wider text-sm transition-colors"
              style={{ textShadow: '0 0 6px rgba(0,240,255,0.3)' }}
            >
              {isRegister 
                ? (isItalian ? 'Accedi' : 'Sign In')
                : (isItalian ? 'Registrati' : 'Register')
              }
            </button>
          </p>
          </>
          )}
        </motion.div>
      </div>

      {/* Right - Cyberpunk Arena Panel */}
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden">
        {/* eSports background image */}
        <img
          src="https://images.pexels.com/photos/6125330/pexels-photo-6125330.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Cyan/Magenta overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#00F0FF]/20 via-[#050508]/70 to-[#FF00E5]/15" />
        {/* Dark gradient from left for blending */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#050508] via-[#050508]/40 to-transparent" />
        {/* Scanline overlay */}
        <div className="absolute inset-0 scanline-overlay pointer-events-none" />
        {/* Grid overlay */}
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center p-12 text-white w-full">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-center"
          >
            <h2 className="font-heading text-3xl xl:text-4xl font-black uppercase tracking-tight mb-4 neon-text-cyan">
              EPT ITALY
            </h2>
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-[#00F0FF] to-transparent mx-auto mb-6" />
            <p className="text-lg text-white/70 max-w-sm mx-auto leading-relaxed">
              {isItalian 
                ? 'Unisciti alla community competitiva di eSports piu grande d\'Italia!'
                : 'Join the largest competitive eSports community in Italy!'
              }
            </p>
            
            {/* Decorative stats */}
            <div className="flex items-center justify-center gap-8 mt-10">
              <div className="text-center">
                <p className="font-mono text-2xl font-bold text-[#00FF87]" style={{ textShadow: '0 0 10px rgba(0,255,135,0.5)' }}>1M+</p>
                <p className="text-xs text-white/40 uppercase tracking-wider mt-1">Budget</p>
              </div>
              <div className="w-px h-8 bg-[#00F0FF]/20" />
              <div className="text-center">
                <p className="font-mono text-2xl font-bold neon-text-magenta">Live</p>
                <p className="text-xs text-white/40 uppercase tracking-wider mt-1">Partite</p>
              </div>
              <div className="w-px h-8 bg-[#00F0FF]/20" />
              <div className="text-center">
                <p className="font-mono text-2xl font-bold neon-text-cyan">24/7</p>
                <p className="text-xs text-white/40 uppercase tracking-wider mt-1">Online</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

const Register = Login;

export { Login, Register };
